import React, { useState, useRef, useEffect } from 'react';
import StorefrontIcon from '@mui/icons-material/Storefront';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import { searchBrands, setBrands } from 'api_handlers/auth';

export default function BrandMultiSelect({ user, onChange }) {
  const initial = user.brands || [];
  const [selectedIds, setSelectedIds] = useState(new Set(initial.map((b) => b.id)));
  // Full objects for the selected brands, so they can be rendered pinned at the
  // top even when the current search results don't contain them.
  const [selectedBrands, setSelectedBrands] = useState(initial);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const searchTimer = useRef(null);
  const ref = useRef(null);

  const fetchBrands = (value) => {
    setLoading(true);
    searchBrands(value)
      .then((res) => setOptions(res.data?.results || []))
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  };

  const handleInput = (e) => {
    const value = e.target.value;
    setInputValue(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchBrands(value), 300);
  };

  const openDropdown = () => {
    setOpen(true);
    if (options.length === 0) fetchBrands('');
  };

  const toggleBrand = (brand) => {
    if (saving) return;
    const prevIds = selectedIds;
    const prevBrands = selectedBrands;
    const next = new Set(selectedIds);
    let nextBrands;
    if (next.has(brand.id)) {
      next.delete(brand.id);
      nextBrands = selectedBrands.filter((b) => b.id !== brand.id);
    } else {
      next.add(brand.id);
      nextBrands = [...selectedBrands, brand];
    }
    // Update both optimistically so the row moves into/out of the pinned
    // section immediately rather than after the round trip.
    setSelectedIds(next);
    setSelectedBrands(nextBrands);
    setSaving(true);
    setBrands(Array.from(next))
      .then((res) => {
        const updatedBrands = res.data?.brands || [];
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify({ ...stored, brands: updatedBrands }));
        setSelectedIds(new Set(updatedBrands.map((b) => b.id)));
        setSelectedBrands(updatedBrands);
        if (onChange) onChange(updatedBrands);
      })
      .catch(() => {
        setSelectedIds(prevIds);
        setSelectedBrands(prevBrands);
      })
      .finally(() => setSaving(false));
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setInputValue('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Selected brands are pinned above the rest so they are always visible and
  // one click from being deselected. The search box filters them client-side
  // so typing narrows the whole list, not just the unselected half.
  const query = inputValue.trim().toLowerCase();
  const pinnedBrands = query
    ? selectedBrands.filter((b) => (b.name || '').toLowerCase().includes(query))
    : selectedBrands;
  const unselectedOptions = options.filter((b) => !selectedIds.has(b.id));
  const isEmpty = pinnedBrands.length === 0 && unselectedOptions.length === 0;

  const sectionLabelStyle = {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: '#999',
    padding: '6px 12px 3px',
  };

  const renderRow = (b) => (
    <label
      key={b.id}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '7px 12px', fontSize: 12, cursor: saving ? 'default' : 'pointer',
        color: '#1a1a1a',
      }}
    >
      <input
        type="checkbox"
        checked={selectedIds.has(b.id)}
        onChange={() => toggleBrand(b)}
        disabled={saving}
      />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</span>
    </label>
  );

  return (
    <div ref={ref} style={{ position: 'relative', marginLeft: 10 }}>
      <button
        onClick={openDropdown}
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: '5px 10px',
          border: '1.5px solid #FEBD1C',
          borderRadius: 20,
          background: open ? '#fff7e3' : 'transparent',
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          color: '#1a1a1a',
        }}
      >
        <StorefrontIcon sx={{ fontSize: 14, color: '#FEBD1C' }} />
        <span style={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {selectedIds.size ? `${selectedIds.size} brands selected` : 'Select brands'}
        </span>
        <KeyboardArrowDownIcon sx={{ fontSize: 14, color: '#FEBD1C', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          width: 240, background: '#fff',
          border: '1.5px solid #FEBD1C',
          borderRadius: 10, boxShadow: '0 4px 20px rgba(254,189,28,0.25)',
          zIndex: 1400, overflow: 'hidden',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderBottom: '1px solid rgba(254,189,28,0.3)' }}>
            <SearchIcon sx={{ fontSize: 14, color: '#FEBD1C' }} />
            <input
              autoFocus
              value={inputValue}
              onChange={handleInput}
              placeholder="Search brands…"
              style={{
                border: 'none', outline: 'none', fontSize: 12,
                background: 'transparent', width: '100%', color: '#1a1a1a',
              }}
            />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {pinnedBrands.length > 0 && (
              <>
                <div style={sectionLabelStyle}>Selected</div>
                {pinnedBrands.map(renderRow)}
                <div style={{ borderTop: '1px solid rgba(254,189,28,0.3)', margin: '4px 0' }} />
              </>
            )}
            {loading && (
              <div style={{ fontSize: 12, color: '#999', padding: '10px 12px' }}>Loading…</div>
            )}
            {!loading && isEmpty && (
              <div style={{ fontSize: 12, color: '#999', padding: '10px 12px' }}>No brands found</div>
            )}
            {!loading && unselectedOptions.length > 0 && (
              <>
                {pinnedBrands.length > 0 && (
                  <div style={sectionLabelStyle}>All brands</div>
                )}
                {unselectedOptions.map(renderRow)}
              </>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#666', padding: '6px 12px', borderTop: '1px solid #f0f0f0' }}>
            {selectedIds.size} selected
          </div>
        </div>
      )}
    </div>
  );
}
